import React from 'react';
import { Campaign } from '../../types/Campaign';
import { CalendarIcon, DollarSignIcon, TagIcon, PercentIcon, ArrowRightIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="overflow-x-auto" data-id="element-1376">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1377">
        <thead className="bg-gray-50" data-id="element-1378">
          <tr data-id="element-1379">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1380">
              Campaign
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1381">
              Code
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1382">
              Discount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1383">
              Budget Usage
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1384">
              Expiry
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1385">
              Status
            </th>
            <th scope="col" className="relative px-6 py-3" data-id="element-1386">
              <span className="sr-only" data-id="element-1387">View</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1388">
          {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onCampaignClick(campaign.id)} data-id="element-1389">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1390">
                  <div className="flex items-center" data-id="element-1391">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1392">
                      <TagIcon size={18} className="text-blue-600" data-id="element-1393" />
                    </div>
                    <div className="ml-4" data-id="element-1394">
                      <div className="text-sm font-medium text-gray-900" data-id="element-1395">
                        {campaign.title}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1396">
                        {campaign.category}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1397">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1398">
                    {campaign.code}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-1399">
                    {campaign.codeStrategy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1400">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-1401">
                    {campaign.discountPercentage > 0 ? <>
                        <PercentIcon size={16} className="mr-1 text-purple-500" data-id="element-1402" />
                        {campaign.discountPercentage}%
                      </> : <>
                        <DollarSignIcon size={16} className="mr-1 text-green-500" data-id="element-1403" />
                        {campaign.fixedDiscountAmount} {campaign.currency}
                      </>}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-1404">
                    {campaign.discountCarriedBy}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1405">
                  <div className="flex items-center" data-id="element-1406">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-1407">
                      <div className="bg-blue-600 h-2.5 rounded-full" style={{
                  width: `${calculateBudgetUsage(campaign)}%`
                }} data-id="element-1408"></div>
                    </div>
                    <span className="text-sm text-gray-500" data-id="element-1409">
                      {calculateBudgetUsage(campaign)}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-1410">
                    {campaign.budgetUsed.toLocaleString()} /{' '}
                    {campaign.budget.toLocaleString()} {campaign.currency}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1411">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-1412">
                    <CalendarIcon size={16} className="mr-1 text-gray-500" data-id="element-1413" />
                    {campaign.expiryDate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1414">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(campaign.status)}`} data-id="element-1415">
                    {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1416">
                  <button className="text-blue-600 hover:text-blue-900" data-id="element-1417">
                    <ArrowRightIcon size={16} data-id="element-1418" />
                  </button>
                </td>
              </tr>) : <tr data-id="element-1419">
              <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1420">
                No campaigns found
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
}